import React, { FC } from "react";
import { Box, TextField, Typography } from "@mui/material";
import Button from "@mui/material/Button";
import { HAMASPIK_LABEL_SX } from "../../../../../assets/hamaspik/theme";
import Badge from "../../../../../assets/hamaspik/Badge";
import { PifFlow, PifFormValues } from "../../types";
import { isValidLicenseNumber } from "../../pifValidation";
import PifSectionCard from "./PifSectionCard";

interface PifSectionContactProps {
  flow: PifFlow;
  emailRequired: boolean;
  /** From position-rules — the licence questions only apply where driving is allowed. */
  mayOperateVehicle: boolean;
  values: PifFormValues;
  onChange: (patch: Partial<PifFormValues>) => void;
}

const CONTACT_NOTE: Record<PifFlow, string> = {
  default: "",
  new: "At least one phone number is required.",
  active: "Read-only — the employee maintains these in self-service.",
  inactive: "Editable again — people move between jobs. Worth confirming.",
};

const PifSectionContact: FC<PifSectionContactProps> = ({ flow, emailRequired, mayOperateVehicle, values: f, onChange }) => {
  const lockContact = flow === "active";
  const phoneMissing = !f.home && !f.cell && flow !== "active";
  const showLicense = f.hasLicense === "yes";
  const licOk = isValidLicenseNumber(f.licNum);

  return (
    <PifSectionCard
      id="sec-contact"
      title="Contact & address"
      note={CONTACT_NOTE[flow]}
      badge={lockContact ? <Badge size="sm">LOCKED</Badge> : undefined}
    >
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 1.75 }}>
        <Field label="Street address *">
          <TextField size="small" fullWidth value={f.addr1} disabled={lockContact} onChange={(e) => onChange({ addr1: e.target.value })} />
        </Field>
        <Field label="City *">
          <TextField size="small" fullWidth value={f.city} disabled={lockContact} onChange={(e) => onChange({ city: e.target.value })} />
        </Field>
        <Field label="State *">
          <TextField size="small" fullWidth value={f.state} disabled={lockContact} onChange={(e) => onChange({ state: e.target.value })} />
        </Field>
        <Field label="ZIP *">
          <TextField size="small" fullWidth value={f.zip} disabled={lockContact} onChange={(e) => onChange({ zip: e.target.value })} />
        </Field>
      </Box>

      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 1.75 }}>
        <Field label="Home phone">
          <TextField size="small" fullWidth value={f.home} disabled={lockContact} placeholder="(718) 000-0000" onChange={(e) => onChange({ home: e.target.value })} />
        </Field>
        <Field label="Cell phone">
          <TextField size="small" fullWidth value={f.cell} disabled={lockContact} placeholder="(845) 000-0000" onChange={(e) => onChange({ cell: e.target.value })} />
          {phoneMissing && (
            <Typography variant="caption" color="warning.main">
              One phone number is required — home or cell.
            </Typography>
          )}
        </Field>
        <Field label={`Personal email ${emailRequired && flow !== "active" ? "*" : ""}`}>
          <TextField size="small" fullWidth value={f.email} disabled={lockContact} placeholder="name@example.com" onChange={(e) => onChange({ email: e.target.value })} />
        </Field>
      </Box>

      {mayOperateVehicle && (
      <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "flex-end", gap: 2.25, pt: 0.75, borderTop: "1px solid", borderColor: "divider", mt: 0.25 }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1, pt: 1.75 }}>
          <Typography sx={HAMASPIK_LABEL_SX}>Holds a driver&apos;s license? *</Typography>
          <Box sx={{ display: "flex", gap: 1.25 }}>
            <Button variant={f.hasLicense === "yes" ? "contained" : "outlined"} size="small" onClick={() => onChange({ hasLicense: "yes" })}>
              Yes
            </Button>
            <Button
              variant={f.hasLicense === "no" ? "contained" : "outlined"}
              color={f.hasLicense === "no" ? "secondary" : "primary"}
              size="small"
              onClick={() => onChange({ hasLicense: "no", licNum: "" })}
            >
              No
            </Button>
          </Box>
        </Box>
        {showLicense && (
          <Box sx={{ display: "flex", gap: 1.75, flex: 1, minWidth: 340, pt: 1.75 }}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1, width: 170 }}>
              <Typography sx={HAMASPIK_LABEL_SX}>Issuing state</Typography>
              <TextField size="small" fullWidth disabled value="New York" />
            </Box>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1, flex: 1 }}>
              <Typography sx={HAMASPIK_LABEL_SX}>License number *</Typography>
              <TextField size="small" fullWidth value={f.licNum} placeholder="9 digits, or 1 letter + 18 digits" onChange={(e) => onChange({ licNum: e.target.value })} />
              {!!f.licNum && !licOk && (
                <Typography variant="caption" color="error.main">
                  Doesn&apos;t match the New York format yet.
                </Typography>
              )}
            </Box>
          </Box>
        )}
      </Box>
      )}
    </PifSectionCard>
  );
};

const Field: FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
    <Typography sx={HAMASPIK_LABEL_SX}>{label}</Typography>
    {children}
  </Box>
);

export default PifSectionContact;
